<template>
<div id="login_page" class="login-shell" dir="rtl">
  <!-- Password reset dialog -->
  <md-dialog-prompt
    :md-active.sync="password_active"
    v-model="resetEmail"
    md-title="איפוס סיסמה"
    md-input-placeholder="הזן את המייל שלך"
    @md-confirm="onConfirmPass()"
    md-confirm-text="שלח"
    md-cancel-text="בטל"/>

  <!-- Split layout -->
  <div class="login-split">

    <!-- Left: cinematic hero panel -->
    <div class="login-hero">
      <div class="login-hero__text">
        <h1 class="login-hero__headline">
          <span class="login-hero__headline-white">הידע שלא תמצאו</span><br>
          <span class="login-hero__headline-red">בשום מקום אחר</span>
        </h1>
        <div class="login-hero__divider" aria-hidden="true"></div>
        <p class="login-hero__sub">
          ההרצאות, הפודקאסטים והתוכן של ערוץ TOV —<br>
          עכשיו בפלטפורמת סטרימינג פרימיום אחת.
        </p>
        <p class="login-hero__categories">
          היסטוריה. ביטחון. גיאופוליטיקה. יהדות. משפט. פוליטיקה. מלחמות. ראיונות גדולים.
        </p>
        <p class="login-hero__promo">
          ללא פרסומות. לפני כולם.
          <span class="login-hero__promo-link" @click="switchTab('signup')">עם גישה לתוכן בלעדי</span>
          שלא עולה ליוטיוב.
        </p>
        
        <div class="login-hero__actions">
          <button class="login-hero__btn-primary" @click="switchTab('signup')">
            הצטרפו עכשיו
             <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <button class="login-hero__btn-secondary" @click="switchTab('login')">
            לצפייה בתכנים
            <svg width="16" height="16" viewBox="0 0 24 24" style=" transform:rotate(180deg)" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
          </button>
        </div>
      </div>
      <div class="login-hero__benefits">
          <div class="login-hero__benefit-item">
            <span class="benefit-icon">📺</span>
            <span>צפייה מכל מכשיר</span>
          </div>
          <div class="login-hero__benefit-item">
            <span class="benefit-icon">🗓️</span>
            <span>תוכן חדש מדי שבוע</span>
          </div>
          <div class="login-hero__benefit-item">
            <span class="benefit-icon">👑</span>
            <span>גישה מלאה לחברי המועדון</span>
          </div>
        </div>
    </div>

    <!-- Right: form panel -->
    <div class="login-form-panel">

      <!-- Close button -->
      <button
        type="button"
        class="login-close"
        aria-label="סגור"
        @click="close_login()"
      >
        <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>

      <div class="login-form-panel__inner">

        <!-- Error banner -->
        <div
          v-if="resetErrorBanner"
          class="login-banner login-banner--error"
          role="alert"
          aria-live="assertive"
        >{{ resetErrorBanner }}</div>

        <!-- Tab bar -->
        <div class="login-tabs" role="tablist" aria-label="מצב כניסה">
          <button
            role="tab"
            :aria-selected="activeTab === 'login'"
            :class="['login-tab', { 'login-tab--active': activeTab === 'login' }]"
            id="tab-login"
            aria-controls="panel-login"
            @click="switchTab('login')"
          >התחברות</button>
          <button
            role="tab"
            :aria-selected="activeTab === 'signup'"
            :class="['login-tab', { 'login-tab--active': activeTab === 'signup' }]"
            id="tab-signup"
            aria-controls="panel-signup"
            @click="switchTab('signup')"
          >הרשמה</button>
        </div>

        <!-- Login panel -->
        <div
          id="panel-login"
          role="tabpanel"
          aria-labelledby="tab-login"
          v-show="activeTab === 'login'"
          :aria-hidden="String(activeTab !== 'login')"
        >
          <LoginPanel
            v-if="!forgotPasswordActive"
            :email-taken-banner="emailTakenBanner"
            :prefill-email="loginPrefillEmail"
            @switch-tab="switchTab"
            @forgot-password="onForgotPassword"
            @clear-email-taken="emailTakenBanner = false"
          />

          <div v-if="forgotPasswordActive" class="login-forgot-section">
            <h3>שחזור סיסמה</h3>
            <p>הזן במייל שלך כדי לקבל קישור לאיפוס סיסמה.</p>

            <form @submit.prevent="submitForgotPassword" class="login-forgot-form">
              <label for="forgot-email" class="login-forgot-label">מייל</label>
              <input
                id="forgot-email"
                type="email"
                v-model="resetEmail"
                class="login-forgot-input"
                placeholder="הזן כתובת מייל"
                aria-required="true"
              />
              <button type="submit" class="login-forgot-submit">שלח קישור לאיפוס</button>
            </form>

            <div v-if="forgotPasswordMessage" class="login-forgot-success" role="status">
              {{ forgotPasswordMessage }}
            </div>

            <button type="button" class="login-forgot-cancel" @click="forgotPasswordActive = false">
              חזור להתחברות
            </button>
          </div>
        </div>

        <!-- Signup panel -->
        <div
          id="panel-signup"
          role="tabpanel"
          aria-labelledby="tab-signup"
          v-show="activeTab === 'signup'"
          :aria-hidden="String(activeTab !== 'signup')"
        >
          <SignupPanel
            ref="signupPanel"
            :initial-step="login_initial_step"
            :initial-plan-id="login_initial_plan_id"
            @switch-tab="switchTab"
            @email-taken="onEmailTaken"
          />
        </div>

        <!-- Trust strip (mobile only — shown below form on mobile) -->
        <div class="login-trust-strip" aria-hidden="true">
          <span>ללא התחייבות</span>
          <span class="trust-dot">·</span>
          <span>בטל בכל עת</span>
          <span class="trust-dot">·</span>
          <span>תשלום מאובטח</span>
        </div>

      </div>
    </div>
  </div>
</div>
</template>

<script>
import LoginPanel from './login/LoginPanel.vue';
import SignupPanel from './login/SignupPanel.vue';

export default {
  props: ['initial_tab', 'initial_step', 'initial_plan_id'],

  components: {
    LoginPanel,
    SignupPanel,
  },

  data() {
    return {
      password_active: false,
      resetEmail: '',
      resetErrorBanner: '',
      forgotPasswordActive: false,
      forgotPasswordMessage: '',
      activeTab: 'login',
      emailTakenBanner: false,
      loginPrefillEmail: '',
      login_initial_step: 1,
      login_initial_plan_id: 0,
    };
  },

  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      this.emailTakenBanner = false;
      if (tab === 'login') {
        this.forgotPasswordActive = false;
        this.resetErrorBanner = '';
        this.forgotPasswordMessage = '';
      }
    },

    onForgotPassword(email) {
      this.forgotPasswordActive = true;
      this.forgotPasswordMessage = '';
      this.resetErrorBanner = '';
      this.resetEmail = email || '';
      this.activeTab = 'login';
    },

    submitForgotPassword() {
      this.resetErrorBanner = '';
      this.forgotPasswordMessage = '';
      if (!this.resetEmail || !this.resetEmail.trim()) {
        this.resetErrorBanner = 'אנא הזן/י כתובת מייל תקינה.';
        return;
      }
      this.api_call({
        url: 'password_reset',
        data: { email: this.resetEmail },
        onComplete: (data) => {
          const payload = data && data.data && data.data.data;
          if (payload && payload.success) {
            this.forgotPasswordMessage = 'בקשת איפוס התקבלה. בדוק/י את המייל לקבלת קישור לאיפוס.';
          } else if (payload && payload.reason === 'send_failed') {
            this.resetErrorBanner = 'המייל קיים, אך לא ניתן לשלוח את הקישור כרגע. נסה/י שוב מעט מאוחר יותר.';
          } else {
            this.resetErrorBanner = 'המייל לא נמצא במערכת';
          }
        },
      }).catch(() => {
        this.resetErrorBanner = 'אירעה שגיאה בשרת. נסה/י שוב.';
      });
    },

    onEmailTaken(email) {
      this.loginPrefillEmail = email;
      this.switchTab('login');
      this.emailTakenBanner = true;
    },

    onConfirmPass() {
      this.resetErrorBanner = '';
      this.forgotPasswordMessage = '';
      this.api_call({
        url: 'password_reset',
        data: { email: this.resetEmail },
        onComplete: (data) => {
          const payload = data && data.data && data.data.data;
          if (payload && payload.success) {
            this.forgotPasswordMessage = 'בקשת איפוס התקבלה. בדוק/י את המייל לקבלת קישור לאיפוס.';
            this.forgotPasswordActive = true;
          } else if (payload && payload.reason === 'send_failed') {
            this.resetErrorBanner = 'המייל קיים, אך לא ניתן לשלוח את הקישור כרגע. נסה/י שוב מעט מאוחר יותר.';
          } else {
            this.resetErrorBanner = 'המייל לא נמצא במערכת';
          }
        },
      }).catch(() => {
        this.resetErrorBanner = 'אירעה שגיאה בשרת. נסה/י שוב.';
      });
    },

    close_login() {
      if (this.$router.history.current.path === '/login') {
        this.$router.push({ path: '/' });
      } else {
        this.$emit('hide_login');
      }
    },
  },

  mounted() {
    if (this.initial_tab)       this.activeTab          = this.initial_tab;
    if (this.initial_step)      this.login_initial_step = this.initial_step;
    if (this.initial_plan_id && this.initial_plan_id > 0)
                                this.login_initial_plan_id = this.initial_plan_id;
  },
};
</script>

<style scoped lang="scss">
/* ── Shell ── */
.login-shell {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: var(--color-bg);
  overflow-y: auto;
}

/* ── Split layout ── */
.login-split {
  display: flex;
  min-height: 100vh;
  flex-direction: row-reverse; /* RTL: form on right (logical start), hero on left */
}

/* ── Hero panel ── */
.login-hero {
  flex: 1;
    height: 100vh;
    position: sticky;
    top: 0;
    padding: 20px;
    max-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: var(--space-8);
    background: linear-gradient(160deg, #0d0d0d 0%, #020313 60%, #04061a 100%);
    overflow: hidden;
}

/* ── Hero: text column ── */
.login-hero__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-height:fit-content;
  gap: var(--space-5);
  max-width: fit-content;
}

.login-hero__headline {
  margin: 0;
  font-family: var(--font-hebrew, var(--font-sans));
  font-size: 64px;
  font-weight: 800;
  line-height: 1.15;
  text-align: start;
}

.login-hero__headline-white {
  color: #ffffff;
}

.login-hero__headline-red {
  color: var(--color-accent, #e11d2a);
}

.login-hero__divider {
  width: 64px;
  height: 3px;
  background: var(--color-accent, #e11d2a);
  border-radius: 2px;

  flex-shrink: 0;
}

.login-hero__sub {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 22px;
  font-weight: 400;
  color: #c8c8c8;
  line-height: 1.6;
}

.login-hero__categories {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 16px;
  color: #888;
  line-height: 1.6;
}

.login-hero__promo {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 16px;
  color: #aaa;
  line-height: 1.6;
}

.login-hero__benefits {
  display: flex;
  flex-wrap: nowrap;
  gap: var(--space-4);
  margin-block-start: var(--space-5);
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: var(--radius-lg, 16px);
  color: #ffffff;
  padding:10px 0;
  max-width: fit-content !important;
  min-width: 90% !important;
}

.login-hero__benefit-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 5px 18px;
  border-right: 1px solid rgba(255,255,255,0.12);
  color: #ffffff;
  min-width: min(250px, 100%);
  font-size: 15px;
}
.login-hero__benefit-item:first-child {
  border-right: none !important;
}

.benefit-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(225, 29, 42, 0.18);
  color: var(--color-accent, #e11d2a);
  font-size: 16px;
}

.login-hero__promo-link {
  color: var(--color-accent, #e11d2a);
  cursor: pointer;
  font-weight: 600;
}

.login-hero__promo-link:hover {
  text-decoration: underline;
}

.login-hero__actions {
  display: flex;
  flex-direction: row;
  gap: var(--space-4);
  flex-wrap: wrap;
  margin-block-start: var(--space-2);
}

.login-hero__btn-primary {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0 var(--space-6);
  height: 52px;
  background: var(--color-accent, #e11d2a);
  color: #fff;
  border: none;
  border-radius: var(--radius-sm, 6px);
  font-family: var(--font-hebrew, var(--font-sans));
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: background 150ms ease, transform 100ms ease;
}

.login-hero__btn-primary:hover {
  background: #c8111f;
  transform: translateY(-1px);
}

.login-hero__btn-secondary {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0 var(--space-6);
  height: 52px;
  background: transparent;
  color: #fff;
  border: 2px solid rgba(255,255,255,0.35);
  border-radius: var(--radius-sm, 6px);
  font-family: var(--font-hebrew, var(--font-sans));
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease;
}

.login-hero__btn-secondary:hover {
  border-color: rgba(255,255,255,0.7);
  background: rgba(255,255,255,0.06);
}

/* ── Hero: visual column ── */
.login-hero__visual {
  flex: 0 0 auto;
  width: 50%;
  max-width: 520px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-hero__gallery {
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: var(--radius-md, 12px);
  display: block;
}

.trust-dot {
  color: var(--color-borderHi);
}

/* ── Form panel ── */
.login-form-panel {
  width: 50%;
  max-width: 560px;
  min-width: 360px;
  background: var(--color-bg);
  border-inline-start: 1px solid var(--color-border);
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.login-form-panel__inner {
  padding: var(--space-12);
  width: 100%;
  box-sizing: border-box;
  max-width: 480px;
  margin-inline: auto;
}

/* ── Close button ── */
.login-close {
  position: absolute;
  inset-block-start: var(--space-4);
  inset-inline-start: var(--space-4);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid var(--color-borderHi);
  background: var(--color-surface2);
  color: var(--color-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 150ms ease, background 150ms ease;
  z-index: 10;
}

.login-close:hover {
  border-color: var(--color-accent);
  background: var(--color-surface3);
}

.login-close:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* ── Forgot password section ── */
.login-forgot-section {
  margin-top: var(--space-6);
  padding: var(--space-5);
 
  border-radius: var(--radius-md, 14px);
}

.login-forgot-section h3 {
  margin: 0 0 var(--space-3) 0;
  color: #ffffff;
  font-size: 20px;
}

.login-forgot-section p {
  margin: 0 0 var(--space-4) 0;
  color: var(--color-textMuted);
  line-height: 1.6;
}

.login-forgot-form {
  display: grid;
  gap: var(--space-3);
}

.login-forgot-label {
  color: var(--color-textMuted);
  font-size: 14px;
  font-weight: 600;
}

.login-forgot-input {
  width: 100%;
  min-height: 48px;
  padding: 12px 14px;
  border-radius: var(--radius-sm, 8px);
  border: 1px solid rgba(255,255,255,0.14);
  background: rgba(255,255,255,0.06);
  color: #fff;
}

.login-forgot-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 4px rgba(225,29,42,0.12);
}

.login-forgot-submit {
  width: 100%;
  min-height: 50px;
  background: var(--color-accent);
  color: #fff;
  border: none;
  border-radius: var(--radius-sm, 8px);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.login-forgot-submit:hover {
  background: #c8111f;
}

.login-forgot-success {
  margin-top: var(--space-4);
  color: #8be685;
  font-weight: 600;
}

.login-forgot-cancel {
  margin-top: var(--space-4);
  background: transparent;
  border: none;
  color: var(--color-textMuted);
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
}

.login-forgot-cancel:hover {
  color: var(--color-text);
}

/* ── Error banner ── */
.login-banner {
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-sm);
  font-size: var(--text-base);
  margin-block-end: var(--space-4);
  line-height: 1.5;
}

.login-banner--error {
  background-color: rgba(224, 65, 65, 0.12);
  border: 1px solid rgba(224, 65, 65, 0.33);
  color: #ffffff;
}

/* ── Tab bar ── */
.login-tabs {
  display: flex;
  flex-direction: row;
  gap: var(--space-2);
  margin-block-end: var(--space-8);
}

.login-tab {
  flex: 1;
  height: 48px;
  font-size: var(--text-lg);
  font-family: var(--font-hebrew);
  font-weight: 600;
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  cursor: pointer;
  color: var(--color-textMuted);
  transition: color 200ms ease, background 200ms ease, border-color 200ms ease;
}

.login-tab--active {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #ffffff;
  box-shadow: 0 0 16px rgba(225, 29, 42, 0.35);
}

.login-tab:hover:not(.login-tab--active) {
  background: var(--color-surface3);
  border-color: var(--color-borderHi);
  color: var(--color-text);
}

.login-tab:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* ── Trust strip (mobile) ── */
.login-trust-strip {
  display: none;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  margin-block-start: var(--space-8);
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--color-textDim);
  flex-wrap: wrap;
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {
  .login-tab,
  .login-close {
    transition: none;
  }
}

/* ── Tablet / narrow desktop: collapse hero ── */
@media (max-width: 980px) {
  .login-split {
    flex-direction: column;
  }

  .login-hero {
    flex: none;
    flex-direction: column;
    padding: var(--space-8) var(--space-6);
    gap: var(--space-6);
  }

  .login-hero__text {
    max-width: 100%;
    align-items: center;
    text-align: center;
  }

  .login-hero__divider {
    margin-inline: auto;
  }

  .login-hero__actions {
    justify-content: center;
  }

  .login-hero__visual {
    width: 100%;
    max-width: 480px;
  }

  .login-form-panel {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    border-inline-start: none;
    border-block-start: 1px solid var(--color-border);
    justify-content: flex-start;
  }

  .login-form-panel__inner {
    max-width: 480px;
    padding: var(--space-8) var(--space-6);
  }

  .login-trust-strip {
    display: flex;
  }
}

/* ── Mobile ── */
@media (max-width: 600px) {
  .login-form-panel__inner {
    padding: var(--space-6) var(--space-4);
  }

  .login-hero__visual {
    display: none;
  }

  .login-hero__categories {
    display: none;
  }
}
</style>
